import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { post } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, PageTitle } from '../components/ui';

export function AccountPage() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState({ current: '', next: '' });
  const [disablePw, setDisablePw] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const startTotp = async () => {
    setError(null);
    try {
      setSetup(await post('/auth/totp/setup'));
    } catch (e) {
      setError(e);
    }
  };
  const confirmTotp = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post('/auth/totp/confirm', { code });
      setSetup(null);
      setCode('');
      setMsg('To-faktor er slået til.');
      await refresh();
    } catch (err) {
      setError(err);
    }
  };
  const disableTotp = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post('/auth/totp/disable', { password: disablePw });
      setDisablePw('');
      setMsg('To-faktor er slået fra.');
      await refresh();
    } catch (err) {
      setError(err);
    }
  };
  const changePassword = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post('/auth/password', { currentPassword: pw.current, newPassword: pw.next });
      await navigate({ to: '/login' });
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <PageTitle title="Min konto" />
      <ErrorBox error={error} />
      {msg && <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{msg}</div>}
      <div className="card text-sm">
        <div>
          <span className="label">Navn</span>
          {me?.name}
        </div>
        <div className="mt-2">
          <span className="label">E-mail</span>
          {me?.email}
        </div>
      </div>

      <div className="card space-y-3">
        <h2 className="text-sm font-semibold">To-faktor (authenticator-app)</h2>
        {me?.totpEnabled ? (
          <form onSubmit={disableTotp} className="flex items-end gap-2">
            <div className="flex-1">
              <label className="label">Adgangskode for at slå fra</label>
              <input className="input" type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} required />
            </div>
            <button className="btn btn-danger">Slå fra</button>
          </form>
        ) : setup ? (
          <form onSubmit={confirmTotp} className="space-y-2">
            <p className="text-sm">Tilføj denne hemmelighed i din authenticator-app (Google Authenticator, 1Password, Microsoft Authenticator):</p>
            <code className="block break-all rounded bg-zinc-100 p-2 text-sm">{setup.secret}</code>
            <a className="text-sm text-blue-700 underline" href={setup.otpauthUrl}>
              Åbn i app (otpauth-link)
            </a>
            <div>
              <label className="label">Kode fra appen</label>
              <input className="input" inputMode="numeric" pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value)} required />
            </div>
            <button className="btn btn-primary">Bekræft</button>
          </form>
        ) : (
          <button className="btn" onClick={startTotp}>
            Slå to-faktor til
          </button>
        )}
      </div>

      <form onSubmit={changePassword} className="card space-y-3">
        <h2 className="text-sm font-semibold">Skift adgangskode</h2>
        <div>
          <label className="label">Nuværende</label>
          <input className="input" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
        </div>
        <div>
          <label className="label">Ny (mindst 12 tegn)</label>
          <input className="input" type="password" autoComplete="new-password" minLength={12} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required />
        </div>
        <button className="btn btn-primary">Skift (du logges ud)</button>
      </form>
    </div>
  );
}
